import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Select from '../ui/Select/Select'
import { isValidIsoDate } from '../../utils/dateHelpers'
import { updateUrlFilters, useUrlFilters } from '../../utils/urlFilters'
import KpiCardsRow from './KpiCardsRow'
import GradeEvolutionChart from './GradeEvolutionChart'
import SectionGradesGrid from './SectionGradesGrid'
import ResourceScopePicker from './ResourceScopePicker'

const GRANULARITY_OPTIONS = [
  { value: 'draft', label: 'Par planification' },
  { value: 'month', label: 'Mensuel' },
  { value: 'quarter', label: 'Trimestriel' },
  { value: 'semester', label: 'Semestriel' },
  { value: 'year', label: 'Annuel' }
]
const [DEFAULT_GRANULARITY] = GRANULARITY_OPTIONS

const MODES = ['grade', 'satisfaction']
const DEFAULT_MODE = 'satisfaction'

const Evolution = ({
  collectId, gradeMax, hasAlertsConfigured, hasAdditionalAnswer,
  resourcesHref, dateRangeMin, dateRangeMax
}) => {
  const filters = useUrlFilters()
  const { start_date: filterStart, end_date: filterEnd } = filters
  const mode = MODES.includes(filters.display) ? filters.display : DEFAULT_MODE
  const [selectedResourceIds, setSelectedResourceIds] = useState([])

  const dateRange = useMemo(() => ({
    startDate: isValidIsoDate(filterStart) ? filterStart : dateRangeMin,
    endDate: isValidIsoDate(filterEnd) ? filterEnd : dateRangeMax
  }), [filterStart, filterEnd, dateRangeMin, dateRangeMax])

  const granularity = useMemo(() => {
    const found = GRANULARITY_OPTIONS.find(option => option.value === filters.granularity)
    return found || DEFAULT_GRANULARITY
  }, [filters.granularity])

  const handleGranularityChange = useCallback(option => {
    const value = option ? option.value : DEFAULT_GRANULARITY.value
    updateUrlFilters({ granularity: value === DEFAULT_GRANULARITY.value ? null : value })
  }, [])

  const handleModeChange = useCallback(nextMode => {
    updateUrlFilters({ display: nextMode === DEFAULT_MODE ? null : nextMode })
  }, [])

  const operatorId = filters.operator || ''
  const resourceSearch = filters.q || ''
  const variableId = filters.variable || ''

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <KpiCardsRow
        collectId={collectId}
        dateRange={dateRange}
        gradeMax={gradeMax}
        hasAdditionalAnswer={hasAdditionalAnswer}
        hasAlertsConfigured={hasAlertsConfigured}
        operatorId={operatorId}
        resourceIds={selectedResourceIds}
        resourceSearch={resourceSearch}
        variableId={variableId}
      />
      <GradeEvolutionChart
        afterChart={(
          <SectionGradesGrid
            collectId={collectId}
            dateRange={dateRange}
            gradeMax={gradeMax}
            granularity={granularity.value}
            mode={mode}
            operatorId={operatorId}
            resourceIds={selectedResourceIds}
            resourcesHref={resourcesHref}
            resourceSearch={resourceSearch}
            variableId={variableId}
          />
        )}
        collectId={collectId}
        dateRange={dateRange}
        gradeMax={gradeMax}
        granularity={granularity.value}
        mode={mode}
        operatorId={operatorId}
        resourceIds={selectedResourceIds}
        resourceSearch={resourceSearch}
        variableId={variableId}
        headerRight={(
          <div className="tw:flex tw:items-center tw:gap-3">
            <ResourceScopePicker
              collectId={collectId}
              selectedResourceIds={selectedResourceIds}
              onChange={setSelectedResourceIds}
            />
            <div className="tw:w-62.5">
              <Select
                isSearchable={false}
                options={GRANULARITY_OPTIONS}
                placeholder="Pas"
                value={granularity}
                onChange={handleGranularityChange}
              />
            </div>
          </div>
        )}
        onModeChange={handleModeChange}
      />
    </div>
  )
}

Evolution.propTypes = {
  collectId: PropTypes.number.isRequired,
  dateRangeMax: PropTypes.string.isRequired,
  dateRangeMin: PropTypes.string.isRequired,
  gradeMax: PropTypes.number,
  hasAdditionalAnswer: PropTypes.bool,
  hasAlertsConfigured: PropTypes.bool,
  resourcesHref: PropTypes.string
}

Evolution.defaultProps = {
  gradeMax: 1,
  hasAdditionalAnswer: false,
  hasAlertsConfigured: false,
  resourcesHref: '#'
}

export default Evolution
